import { startNotificationPrefetch } from '../components/NotificationBell';
import React, { useState, useEffect } from 'react';
import { wantsReturnToProfile, clearReturnToProfile } from '../utils/returnToProfile';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import PropertyCalendar from '../components/PropertyCalendar';
import AutomationOverview from '../components/AutomationOverview';
import CleaningOverview, { prefetchCleaningOverview } from '../components/CleaningOverview';
import CalendarHub from '../components/CalendarHub';
import { PropertyPicker } from '../components/PropertyFilter';
import ErrorBoundary from '../components/ErrorBoundary';
import SmartStaysLogo from '../components/SmartStaysLogo';
import NotificationBell from '../components/NotificationBell';
import ProfileMenu from '../components/ProfileMenu';
import { useAppLayout, useNativeShell } from '../utils/nativeApp';
import AppNav from '../components/AppNav';
import NativeScreenTitle from '../components/NativeScreenTitle';
import API_URL from '../config';
import { getAuthToken, getAuthUser, isImpersonating, stopImpersonating } from '../utils/authSession';
import { validateRequiredFields, clearFieldInvalid } from '../utils/formValidation';
import './Dashboard.css';

function Dashboard() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [currentGuestCode, setCurrentGuestCode] = useState(null);
  const [currentGuestCodeLoading, setCurrentGuestCodeLoading] = useState(false);
  const [personalCodes, setPersonalCodes] = useState([]);
  const [smartlockStatus, setSmartlockStatus] = useState(null);
  const [smartlockStatusLoading, setSmartlockStatusLoading] = useState(false);
  const [smartlockLog, setSmartlockLog] = useState([]);
  const [smartlockLogLoading, setSmartlockLogLoading] = useState(false);
  const [showSmartlockLog, setShowSmartlockLog] = useState(false);
  // Onder "Acties" tonen we standaard enkel de events met een herkenbare
  // naam (gastcode = ingecheckt, een persoonlijke/schoonmaakcode = iemand
  // toegekomen) - anonieme/systeem-events (bv. via de Nuki-app zelf, zonder
  // gekoppelde naam) verdwijnen dan niet, maar komen enkel nog in de losse
  // "Volledige geschiedenis"-lijst hieronder terecht (zie showFullSmartlockLog).
  const [showFullSmartlockLog, setShowFullSmartlockLog] = useState(false);
  // Instellingen van het EIGEN smart lock van de verhuurder (merk + smartlock-
  // id + Nuki API-token) - sinds de verhuurder zelf zijn slot aanschaft/
  // beheert i.p.v. dat SmartStays dit installeert. Het token wordt om
  // veiligheidsredenen nooit teruggestuurd door de server: het invoerveld
  // toont enkel of er al één is ingesteld (via smartlockStatus.hasApiToken),
  // nooit de echte waarde.
  const [showSmartlockConfig, setShowSmartlockConfig] = useState(false);
  const [smartlockConfigForm, setSmartlockConfigForm] = useState({ smartlockBrand: '', nukiSmartlockId: '', nukiApiToken: '' });
  const [smartlockConfigSaving, setSmartlockConfigSaving] = useState(false);
  const [smartlockConfigError, setSmartlockConfigError] = useState('');
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [showAddCodeModal, setShowAddCodeModal] = useState(false);
  const [showEditPropertyModal, setShowEditPropertyModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [editingCodeId, setEditingCodeId] = useState(null);
  const [editingCodeName, setEditingCodeName] = useState('');
  const [newCodeName, setNewCodeName] = useState('');
  const [newCodeValue, setNewCodeValue] = useState('');
  const [confirmCodeValue, setConfirmCodeValue] = useState('');
  const [codeError, setCodeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  // Eigen bevestigingsmodaltje i.p.v. window.confirm() - askConfirm() vult
  // confirmState.message/onConfirm, zie de JSX + helper verderop.
  const [confirmState, setConfirmState] = useState(null);
  const [activeTab, setActiveTab] = useState('smartlock');
  // Welke hoofdweergave getoond wordt: de gewone woningenlijst + per-woning
  // tabs (Smart lock/Kalender/Codes), of de nieuwe multi-kalender (alle
  // woningen samen op één tijdlijn, zoals Guesty's "algemene kalender").
  // Standaard bij het inloggen: de multi-kalender, niet de eerste woning
  // (die anders meteen op zijn/haar smart lock-tab terechtkwam).
  const [mainView, setMainView] = useState(() => (wantsReturnToProfile() ? 'profile' : 'multi-calendar'));
  useEffect(() => { clearReturnToProfile(); }, []);
  // Op mobiel worden de weergave-toggle (Woningen/Multi-kalender) en de
  // woningenlijst niet meer boven elkaar in de navbar gepropt, maar
  // samengebracht in dit uitklapbare hamburgermenu.
  // App-weergave (onderste tabbalk i.p.v. navigatiebalk bovenaan): in de echte
  // iOS/Android-app, en ook in de webapp op een telefoon (smal scherm). Op
  // computer/tablet staat bovenaan de navigatiebalk (AppNav).
  const isNative = useAppLayout();
  useNativeShell(isNative);
  const [nativePropertyDetail, setNativePropertyDetail] = useState(false);
  const [nativeUnread, setNativeUnread] = useState(0);
  useEffect(() => { prefetchCleaningOverview(); return startNotificationPrefetch(); }, []);

  useEffect(() => {
    const reset = () => setNativeUnread(0);
    window.addEventListener('smartstays:notifications-read', reset);
    return () => window.removeEventListener('smartstays:notifications-read', reset);
  }, []);
  useEffect(() => {
    if (!isNative) return undefined;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/notifications/unread-count`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        setNativeUnread(res.data.count || 0);
      } catch (e) { /* stil */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [isNative, mainView]);
  // Native app: het tabblad dat vroeger "Home" was is nu "Smart lock". Met een
  // abonnement toont het enkel woningen die aan toegangsbeheer gekoppeld zijn;
  // zonder abonnement blijft het de gewone woningenlijst (voor bewerken).
  // Slotje enkel voor wie echt een smart lock heeft: abonnement + minstens 1
  // woning met toegangsbeheer (zolang de woningen laden gaan we uit van het
  // abonnement alleen).
  const hasLock = !!user?.hasSubscription
    && (properties.length === 0 || properties.some((p) => p.accessManagementEnabled));
  const nativeLockProperties = hasLock
    ? properties.filter((p) => p.accessManagementEnabled)
    : properties;
  const nativeLockMode = isNative && hasLock;
  // Schoonmaak-menu enkel voor klanten die schoonmaak gekozen hebben (minstens 1 woning
  // met schoonmaak). Zolang de woningen nog laden (lege lijst) blijft het menu staan, zodat
  // de balk niet verspringt.
  const hasCleaning = properties.length === 0 || properties.some((p) => (p.cleaningTier || 'FULL') !== 'NONE');
  useEffect(() => {
    if (!hasCleaning && mainView === 'cleaning') setMainView('multi-calendar');
  }, [hasCleaning, mainView]);
  useEffect(() => {
    if (!isNative || mainView !== 'properties') return;
    if (nativeLockProperties.length === 1 && !nativePropertyDetail) {
      setSelectedProperty(nativeLockProperties[0]);
      setNativePropertyDetail(true);
    }
  }, [isNative, mainView, nativePropertyDetail, nativeLockProperties.length, user?.hasSubscription, properties]); // eslint-disable-line react-hooks/exhaustive-deps
  const [lockActionLoading, setLockActionLoading] = useState(false);
  const [smartlockSyncing, setSmartlockSyncing] = useState(false);
  // Schoonmaakinstructies-PDF van de geselecteerde woning (verving de
  // vroegere checklist) - de verhuurder mag deze net als de admin uploaden/
  // vervangen/verwijderen/bekijken.
  const [cleaningPdfUploading, setCleaningPdfUploading] = useState(false);
  const [cleaningPdfError, setCleaningPdfError] = useState('');
  // Woningfoto (i.p.v. het generieke huis-icoontje) - zelfde patroon als de
  // schoonmaak-PDF hierboven.
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const token = getAuthToken();
    const userData = getAuthUser();

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'VERHUURDER') {
      if (parsedUser.role === 'ADMIN') {
        navigate('/admin');
      } else {
        navigate('/cleaner');
      }
      return;
    }

    setUser(parsedUser);
    fetchProperties();
    fetchPersonalCodes();
    document.title = 'SmartStays - Dashboard';

    // De ingelogde gebruiker in localStorage is een momentopname van het
    // moment van inloggen. Vlaggen die de admin achteraf aanpast (bv.
    // hasSubscription, hasAutomation) staan daar dus niet in totdat de
    // gebruiker opnieuw inlogt. Haal daarom bij elke dashboard-load de
    // actuele gegevens op via /api/me en werk zowel de state als
    // localStorage bij, zodat zo'n wijziging meteen zichtbaar is zonder
    // dat de gebruiker moet uitloggen en opnieuw inloggen.
    (async () => {
      try {
        const response = await axios.get(`${API_URL}/api/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const refreshedUser = { ...parsedUser, ...response.data };
        setUser(refreshedUser);
        // Enkel de "echte" localStorage-sessie bijwerken als we niet aan het
        // impersoneren zijn - anders overschrijft dit ververste (klant-)
        // profiel de eigenlijke admin-sessie in localStorage, waardoor de
        // admin na "Terug naar admin" (wat impersonatie stopt en de admin-
        // sessie uit localStorage teruglegt) toch als klant blijft ogen.
        if (!isImpersonating()) {
          localStorage.setItem('user', JSON.stringify(refreshedUser));
        }
      } catch (err) {
        // Stil falen: de gecachete gebruiker uit localStorage blijft gewoon
        // bruikbaar als /api/me even niet lukt.
      }
    })();
  }, [navigate]);

  // Bepaalt op welke tab een woning moet openen bij het aanklikken. Bewust
  // NOOIT "automation": ook al is dat de meest linkse (eerste) tab-knop bij
  // een woning met een Automation-abonnement, we willen daar niet automatisch
  // op landen - enkel als de gebruiker er zelf expliciet op klikt. Voor
  // woningen zonder toegangsbeheer-koppeling (accessManagementEnabled) is er
  // geen Smart lock-tab, dus daar starten we meteen op Kalender.
  // Moet altijd overeenkomen met de werkelijke volgorde/zichtbaarheid van de
  // tab-knoppen hieronder (Automation, Kalender, Smart lock, Codes) - dus de
  // EERSTE tab die voor déze specifieke woning zichtbaar is, i.p.v. altijd
  // een vaste tab. Kalender staat zelf altijd zichtbaar vóór Smart lock/
  // Codes, dus die twee komen hier nooit als standaard-tab uit; enkel
  // Automation (indien ingeschakeld voor deze woning) gaat vóór Kalender.
  const getDefaultPropertyTab = (property) => (
    user?.hasAutomation && property?.automationEnabled ? 'automation' : 'calendar'
  );

  useEffect(() => {
    if (!selectedProperty) {
      setSmartlockStatus(null);
      setCurrentGuestCode(null);
      setSmartlockLog([]);
      setShowSmartlockLog(false);
      return;
    }
    fetchSmartlockStatus(selectedProperty.id);
    fetchCurrentGuestCode(selectedProperty.id);
    setSmartlockLog([]);
    setShowSmartlockLog(false);
    setActiveTab(user?.hasSubscription && selectedProperty.accessManagementEnabled ? 'smartlock' : 'calendar');
  }, [selectedProperty, user]);

  const fetchSmartlockStatus = async (propertyId) => {
    try {
      setSmartlockStatusLoading(true);
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/properties/${propertyId}/smartlock-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmartlockStatus(response.data);
      setSmartlockConfigForm({
        smartlockBrand: response.data.smartlockBrand || '',
        nukiSmartlockId: response.data.smartlockId || '',
        nukiApiToken: '',
      });
    } catch (error) {
      console.error('Fout bij ophalen smartlock-status:', error);
      setSmartlockStatus(null);
    } finally {
      setSmartlockStatusLoading(false);
    }
  };

  // Slaat het merk + smartlock-id + (optioneel) Nuki API-token van het EIGEN
  // slot van de verhuurder op. Het token-veld wordt enkel meegestuurd als de
  // verhuurder er effectief iets in getypt heeft (leeg laten = het eerder
  // opgeslagen token blijft ongewijzigd) - zo hoeft het bestaande token niet
  // telkens opnieuw ingevuld te worden enkel om het merk of de id te wijzigen.
  const handleSaveSmartlockConfig = async (e) => {
    e.preventDefault();
    if (!selectedProperty) return;
    setSmartlockConfigSaving(true);
    setSmartlockConfigError('');
    try {
      const token = getAuthToken();
      const payload = {
        smartlockBrand: smartlockConfigForm.smartlockBrand || null,
        nukiSmartlockId: smartlockConfigForm.nukiSmartlockId || null,
      };
      if (smartlockConfigForm.nukiApiToken.trim() !== '') {
        payload.nukiApiToken = smartlockConfigForm.nukiApiToken.trim();
      }
      await axios.put(`${API_URL}/api/properties/${selectedProperty.id}/smartlock-config`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast(t('dashboard.smartlockConfigSaved'), 'success');
      setShowSmartlockConfig(false);
      await fetchSmartlockStatus(selectedProperty.id);
    } catch (error) {
      setSmartlockConfigError(error.response?.data?.error || t('dashboard.smartlockConfigError'));
    } finally {
      setSmartlockConfigSaving(false);
    }
  };

  // Vraagt expliciet een verse synchronisatie met Nuki aan (met server-side
  // cooldown, zie backend). Geeft altijd de best-beschikbare status terug,
  // dus deze knop toont nooit een foutmelding - in het slechtste geval was de
  // getoonde status al de meest recente die er is.
  const handleSyncSmartlock = async () => {
    if (!selectedProperty) return;
    setSmartlockSyncing(true);
    try {
      const token = getAuthToken();
      const response = await axios.post(`${API_URL}/api/properties/${selectedProperty.id}/smartlock-sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmartlockStatus(response.data);
    } catch (error) {
      console.error('Fout bij synchroniseren smartlock-status:', error);
    } finally {
      setSmartlockSyncing(false);
    }
  };

  const fetchCurrentGuestCode = async (propertyId) => {
    try {
      setCurrentGuestCodeLoading(true);
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/properties/${propertyId}/current-guest-code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentGuestCode(response.data);
    } catch (error) {
      console.error('Fout bij ophalen huidige gastcode:', error);
      setCurrentGuestCode(null);
    } finally {
      setCurrentGuestCodeLoading(false);
    }
  };

  // Openingsgeschiedenis wordt niet lokaal opgeslagen: telkens live opgehaald
  // bij Nuki (die dit zelf als "Activity log" bijhoudt), enkel wanneer de
  // klant het tabblad effectief opent.
  const fetchSmartlockLog = async (propertyId) => {
    try {
      setSmartlockLogLoading(true);
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/properties/${propertyId}/smartlock-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmartlockLog(response.data.entries || []);
    } catch (error) {
      console.error('Fout bij ophalen openingsgeschiedenis:', error);
      setSmartlockLog([]);
    } finally {
      setSmartlockLogLoading(false);
    }
  };

  // Stuurt een directe ontgrendel/vergrendel-actie naar het slot zelf.
  // action: 1 = ontgrendelen, 2 = vergrendelen (zelfde codes als het activity-log).
  // Werkt de getoonde status (vergrendeld/ontgrendeld) meteen optimistisch bij,
  // en draait terug bij een fout.
  const handleSmartlockAction = async (action) => {
    if (!selectedProperty) return;
    const previousLocked = smartlockStatus?.locked;
    try {
      setLockActionLoading(true);
      setSmartlockStatus((prev) => (prev ? { ...prev, locked: action === 2 } : prev));
      const token = getAuthToken();
      await axios.post(`${API_URL}/api/properties/${selectedProperty.id}/smartlock-action`, { action }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast(t('dashboard.lockActionSuccess'), 'success');
    } catch (error) {
      setSmartlockStatus((prev) => (prev ? { ...prev, locked: previousLocked } : prev));
      showToast(error.response?.data?.error || t('dashboard.lockActionError'), 'error');
    } finally {
      setLockActionLoading(false);
    }
  };

  const toggleSmartlockLog = () => {
    if (!showSmartlockLog && smartlockLog.length === 0) {
      fetchSmartlockLog(selectedProperty.id);
    }
    setShowSmartlockLog(!showSmartlockLog);
  };

  // Ruwe Nuki actie-codes voor lock/unlock-events (bevestigd via de officiële
  // Nuki Web API-documentatie). Andere logtypes (bv. batterij- of
  // configuratiemeldingen) worden gewoon met hun ruwe code getoond zolang dit
  // nog niet tegen een echt gekoppeld slot getest is.
  const NUKI_ACTION_LABELS = {
    1: t('dashboard.actionUnlocked'),
    2: t('dashboard.actionLocked'),
    3: t('dashboard.actionUnlatch'),
    4: t('dashboard.actionLockAndGo'),
    5: t('dashboard.actionLockAndGoUnlatch'),
  };

  // "Belangrijke" acties = elk logitem met een herkenbare naam (Nuki vult
  // deze enkel in wanneer een naamgegeven code/gebruiker de actie deed) - dus
  // in de praktijk exact: de gastcode (inchecken), een schoonmaakcode
  // (toegekomen) of een andere persoonlijke code. Naamloze/systeem-events
  // (bv. rechtstreeks via de Nuki-bridge of een niet-herkende bron) blijven
  // enkel zichtbaar in de volledige geschiedenis hieronder.
  const importantSmartlockLog = smartlockLog.filter((entry) => entry.name);

  // Nette weergavenaam per ondersteund smart lock-merk (ruwe databasewaarde
  // is altijd de hoofdletter-code, bv. "NUKI"/"TEDEE").
  const SMARTLOCK_BRAND_LABELS = {
    NUKI: 'Nuki',
    TEDEE: 'Tedee',
  };

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const askConfirm = (message, onConfirm) => setConfirmState({ message, onConfirm });

  // Zet een gewijzigd veld (bv. na PDF-upload) meteen door naar de
  // woningen-lijst zelf, en - als het de woning is die nu open staat in het
  // bewerk-formulier - ook naar editingProperty, zodat de UI meteen klopt
  // zonder alles opnieuw te moeten ophalen.
  const applyPropertyPatch = (propertyId, patch) => {
    setProperties((prev) => prev.map((p) => (p.id === propertyId ? { ...p, ...patch } : p)));
    setSelectedProperty((prev) => (prev && prev.id === propertyId ? { ...prev, ...patch } : prev));
    setEditingProperty((prev) => (prev && prev.id === propertyId ? { ...prev, ...patch } : prev));
  };

  // Schoonmaakinstructies-PDF: sectie onderaan het "woning bewerken"-
  // formulier (bij de iCal-velden), werkt op de woning die daar open staat.
  const handleUploadCleaningPdf = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !editingProperty) return;
    if (file.type !== 'application/pdf') {
      setCleaningPdfError(t('dashboard.cleaningPdfInvalidType'));
      return;
    }
    setCleaningPdfUploading(true);
    setCleaningPdfError('');
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/properties/${editingProperty.id}/cleaning-pdf`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      applyPropertyPatch(editingProperty.id, {
        cleaningPdfOriginalName: res.data.cleaningPdfOriginalName,
        cleaningPdfUploadedAt: res.data.cleaningPdfUploadedAt
      });
      showToast(t('dashboard.cleaningPdfUploaded'), 'success');
    } catch (error) {
      setCleaningPdfError(error.response?.data?.error || t('dashboard.cleaningPdfUploadError'));
    } finally {
      setCleaningPdfUploading(false);
    }
  };

  const handleDeleteCleaningPdf = async () => {
    if (!editingProperty) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/api/properties/${editingProperty.id}/cleaning-pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      applyPropertyPatch(editingProperty.id, { cleaningPdfOriginalName: null, cleaningPdfUploadedAt: null });
      showToast(t('dashboard.cleaningPdfDeleted'), 'success');
    } catch (error) {
      setCleaningPdfError(error.response?.data?.error || t('dashboard.cleaningPdfDeleteError'));
    }
  };

  const handleViewCleaningPdf = async () => {
    if (!editingProperty) return;
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/api/properties/${editingProperty.id}/cleaning-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      setCleaningPdfError(t('dashboard.cleaningPdfLoadError'));
    }
  };

  // Woningfoto uploaden/verwijderen - zelfde patroon als de schoonmaak-PDF
  // hierboven, werkt op de woning die open staat in het bewerk-formulier.
  const handleUploadPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !editingProperty) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setPhotoError(t('dashboard.photoInvalidType'));
      return;
    }
    setPhotoUploading(true);
    setPhotoError('');
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/properties/${editingProperty.id}/photo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      applyPropertyPatch(editingProperty.id, { photoUrl: res.data.photoUrl });
      showToast(t('dashboard.photoUploaded'), 'success');
    } catch (error) {
      setPhotoError(error.response?.data?.error || t('dashboard.photoUploadError'));
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!editingProperty) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/api/properties/${editingProperty.id}/photo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      applyPropertyPatch(editingProperty.id, { photoUrl: null });
      showToast(t('dashboard.photoDeleted'), 'success');
    } catch (error) {
      setPhotoError(error.response?.data?.error || t('dashboard.photoDeleteError'));
    }
  };

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
      if (response.data.length > 0) {
        setSelectedProperty(response.data[0]);
      }
    } catch (error) {
      console.error('Fout bij ophalen properties:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        if (isImpersonating()) {
          // Enkel de impersonatie-sessie van dit tabblad beëindigen - niet de
          // echte, gedeelde sessie van de admin (localStorage) aanraken.
          stopImpersonating();
          window.location.href = '/admin';
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        }
      }
      showToast(t('dashboard.toastLoadPropertiesFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonalCodes = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/personal-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPersonalCodes(response.data);
    } catch (error) {
      console.error('Fout bij ophalen personal codes:', error);
    }
  };

  const openEditPropertyModal = (property) => {
    setEditingProperty({
      id: property.id,
      name: property.name || '',
      address: property.address || '',
      airbnbIcalUrl: property.airbnbIcalUrl || '',
      bookingIcalUrl: property.bookingIcalUrl || '',
      vrboIcalUrl: property.vrboIcalUrl || '',
      guestyActive: !!property.guestyActive,
      hospitableActive: !!property.hospitableActive,
      cleaningPdfOriginalName: property.cleaningPdfOriginalName || null,
      cleaningPdfUploadedAt: property.cleaningPdfUploadedAt || null,
      photoUrl: property.photoUrl || null
    });
    setCleaningPdfError('');
    setPhotoError('');
    setShowEditPropertyModal(true);
  };

  const handleEditPropertySave = async (e) => {
    e.preventDefault();
    if (!editingProperty) return;
    if (!validateRequiredFields(e.target)) {
      showToast(t('common.fillRequired'), 'error');
      return;
    }

    try {
      const token = getAuthToken();

      await axios.put(`${API_URL}/api/properties/${editingProperty.id}`, {
        name: editingProperty.name,
        address: editingProperty.address
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowEditPropertyModal(false);
      setEditingProperty(null);
      await fetchProperties();
      showToast(t('dashboard.propertyUpdated'), 'success');
    } catch (error) {
      console.error('Fout bij updaten:', error);
      showToast(error.response?.data?.error || t('dashboard.updateError'), 'error');
    }
  };

  const validateCode = (code) => {
    // Zelfde regel als de backend (nukiService.isValidKeypadCode): exact 6
    // cijfers, enkel 1-9 (geen 0), en niet startend met "12".
    if (!/^[1-9]{6}$/.test(code) || code.startsWith('12')) {
      return t('dashboard.codeInvalidRule');
    }
    return null;
  };

  const handleAddCode = async () => {
    const error = validateCode(newCodeValue);
    if (error) {
      setCodeError(error);
      return;
    }

    if (newCodeValue !== confirmCodeValue) {
      setCodeError(t('dashboard.codesNoMatch'));
      return;
    }

    if (personalCodes.length >= 4) {
      setCodeError(t('dashboard.max4Codes'));
      return;
    }

    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await axios.post(`${API_URL}/api/personal-codes`, {
        name: newCodeName || `Code ${personalCodes.length + 1}`,
        code: newCodeValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPersonalCodes([...personalCodes, response.data.code]);
      setShowAddCodeModal(false);
      setNewCodeName('');
      setNewCodeValue('');
      setConfirmCodeValue('');
      setCodeError('');
      showToast(t('dashboard.codeAdded'), 'success');
    } catch (error) {
      setCodeError(error.response?.data?.error || t('dashboard.addCodeError'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditCode = async () => {
    const error = validateCode(newCodeValue);
    if (error) {
      setCodeError(error);
      return;
    }

    if (newCodeValue !== confirmCodeValue) {
      setCodeError(t('dashboard.codesNoMatch'));
      return;
    }

    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await axios.put(`${API_URL}/api/personal-codes/${editingCodeId}`, {
        name: editingCodeName || newCodeName,
        code: newCodeValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPersonalCodes(personalCodes.map(c =>
        c.id === editingCodeId ? response.data.code : c
      ));

      setShowCodeModal(false);
      setEditingCodeId(null);
      setEditingCodeName('');
      setNewCodeName('');
      setNewCodeValue('');
      setConfirmCodeValue('');
      setCodeError('');
      showToast(t('dashboard.codeUpdated'), 'success');
    } catch (error) {
      setCodeError(error.response?.data?.error || t('dashboard.editCodeError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCode = async (codeId, codeName) => {
    askConfirm(t('dashboard.confirmDeleteCode', { name: codeName }), async () => {
      try {
        setLoading(true);
        const token = getAuthToken();
        await axios.delete(`${API_URL}/api/personal-codes/${codeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setPersonalCodes(personalCodes.filter(c => c.id !== codeId));
        showToast(t('dashboard.codeDeleted'), 'success');
      } catch (error) {
        showToast(t('dashboard.deleteError'), 'error');
      } finally {
        setLoading(false);
      }
    });
  };

  const openEditModal = (code) => {
    setEditingCodeId(code.id);
    setEditingCodeName(code.name);
    setNewCodeName(code.name);
    setNewCodeValue(code.code);
    setConfirmCodeValue(code.code);
    setCodeError('');
    setShowCodeModal(true);
  };

  const handleLogout = () => {
    // Loggen we hier eigenlijk enkel de impersonatie-sessie van dit tabblad
    // uit ("Inloggen als" vanuit Admin.js)? Dan keert dit tabblad gewoon
    // terug naar de echte, ondertussen ongewijzigde admin-sessie i.p.v. een
    // volledige uitlog - de admin hoeft dus niet opnieuw in te loggen.
    if (isImpersonating()) {
      stopImpersonating();
      window.location.href = '/admin';
      return;
    }

    const isNative = localStorage.getItem('isNativeApp') === 'true';
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    if (isNative) {
      window.location.href = '/splash';
    } else {
      window.location.href = '/';
    }
  };

  if (!user) return <div aria-busy="true" />;

  const handleNotificationNavigate = (n) => {
    const relatedProperty = n.relatedId && properties.find((p) => p.id === n.relatedId);
    if (!relatedProperty) return;
    setMainView('properties');
    setSelectedProperty(relatedProperty);
    setNativePropertyDetail(true);
    setActiveTab(n.type === 'BATTERY_WARNING' || n.type === 'BATTERY_LOW' ? 'smartlock' : 'calendar');
  };

  // Titel bovenaan in de app: altijd gecentreerd op dezelfde plek.
  const nativeTitle = !isNative ? null
    : mainView === 'multi-calendar' ? t('dashboard.tabCalendar')
    : mainView === 'cleaning' ? t('dashboard.navCleaning')
    : mainView === 'automation' ? t('dashboard.navAutomation')
    : mainView === 'notifications' ? t('notifications.title')
    : mainView === 'profile' ? t('profileMenu.title')
    : mainView === 'my-properties' ? t('profileMenu.properties')
    : mainView === 'properties' ? (nativeLockMode ? t('dashboard.navAccess') : t('dashboard.myProperties'))
    : null;

  return (
    <div className={`dashboard-container ${isNative ? 'dashboard-container-native' : 'dashboard-container-topnav'}`}>
      {isNative ? (
        <nav className="dashboard-nav">
          <div className="nav-brand">
            <SmartStaysLogo className="nav-logo" />
          </div>
          <div className="nav-user"></div>
          <NativeScreenTitle title={nativeTitle} />
        </nav>
      ) : (
        <AppNav
          items={[
            { key: 'multi-calendar', label: t('dashboard.tabCalendar'), active: mainView === 'multi-calendar', onClick: () => setMainView('multi-calendar') },
            ...(hasCleaning ? [{ key: 'cleaning', label: t('dashboard.navCleaning'), active: mainView === 'cleaning', onClick: () => setMainView('cleaning') }] : []),
            ...(user?.hasAutomation ? [{ key: 'automation', label: t('dashboard.navAutomation'), active: mainView === 'automation', onClick: () => setMainView('automation') }] : []),
            { key: 'my-properties', label: t('dashboard.myProperties'), active: mainView === 'my-properties' || mainView === 'properties', onClick: () => setMainView('my-properties') },
          ]}
          right={(
            <>
              <NotificationBell onNotificationClick={handleNotificationNavigate} />
              <ProfileMenu user={user} onLogout={handleLogout} />
            </>
          )}
        />
      )}

      {toast.show && (
        <div className={`toast-message ${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Eigen bevestigingsmodaltje i.p.v. window.confirm() - askConfirm()
          hierboven vult confirmState.message/onConfirm; "Verwijderen" voert
          onConfirm uit en sluit het modaltje. */}
      {confirmState && (
        <div className="modal-overlay" onClick={() => setConfirmState(null)}>
          <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('common.confirm')}</h2>
              <button className="modal-close" onClick={() => setConfirmState(null)}>✖</button>
            </div>
            <div className="modal-body">
              <p style={{ whiteSpace: 'pre-line' }}>{confirmState.message}</p>
            </div>
            <div className="modal-buttons">
              <button type="button" className="cancel-btn" onClick={() => setConfirmState(null)}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="submit-btn"
                onClick={() => { const { onConfirm } = confirmState; setConfirmState(null); onConfirm(); }}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-layout">
        <main className="dashboard-main">
          <ErrorBoundary resetKey={`${mainView}|${activeTab}`} compact>
          {isNative && mainView === 'notifications' ? (
            <NotificationBell screen onNotificationClick={handleNotificationNavigate} />
          ) : isNative && mainView === 'my-properties' ? (
            <div className="native-property-list-wrap">
              <div className="native-detail-head">
                <button type="button" className="native-detail-back" onClick={() => setMainView('profile')} aria-label={t('common.back', 'Terug')}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
                </button>
                <h2 className="native-detail-title">{t('profileMenu.properties')}</h2>
              </div>
              {properties.length === 0 ? (
                <>
                  <p className="empty-hint">{t('dashboard.noProperties')}</p>
                  <p className="empty-hint">{t('dashboard.contactAdmin')}</p>
                </>
              ) : (
                <PropertyPicker properties={properties} onPick={(prop) => openEditPropertyModal(prop)} />
              )}
            </div>
          ) : isNative && mainView === 'profile' ? (
            <ProfileMenu screen user={user} onLogout={handleLogout} onOpenProperties={() => setMainView('my-properties')} />
          ) : isNative && mainView === 'properties' && !nativePropertyDetail ? (
            <div className="native-property-list-wrap">
              {nativeLockProperties.length === 0 ? (
                <>
                  <h2 className="native-screen-title">{nativeLockMode ? t('dashboard.tabSmartlock') : t('dashboard.myProperties')}</h2>
                  <p className="empty-hint">{t('dashboard.noProperties')}</p>
                  <p className="empty-hint">{t('dashboard.contactAdmin')}</p>
                </>
              ) : (
                <PropertyPicker
                  title={nativeLockMode ? t('propertyFilter.pickTitle') : t('dashboard.myProperties')}
                  properties={nativeLockProperties}
                  onPick={(prop) => { setSelectedProperty(prop); setActiveTab(nativeLockMode ? 'smartlock' : getDefaultPropertyTab(prop)); setNativePropertyDetail(true); }}
                />
              )}
            </div>
          ) : !isNative && mainView === 'my-properties' ? (
            properties.length === 0 ? (
              <>
                <p className="empty-hint">{t('dashboard.noProperties')}</p>
                <p className="empty-hint">{t('dashboard.contactAdmin')}</p>
              </>
            ) : (
              <PropertyPicker
                grid
                title={t('dashboard.myProperties')}
                properties={properties}
                onPick={(prop) => { setSelectedProperty(prop); setActiveTab(getDefaultPropertyTab(prop)); setMainView('properties'); }}
                onEdit={(prop) => openEditPropertyModal(prop)}
              />
            )
          ) : mainView === 'multi-calendar' ? (
            <CalendarHub properties={properties} userRole={user.role} />
          ) : mainView === 'cleaning' ? (
            <CleaningOverview showFilter={isNative} />
          ) : mainView === 'automation' && user.hasAutomation ? (
            <AutomationOverview properties={isNative ? properties : undefined} />
          ) : selectedProperty ? (
            <>
              {!isNative && properties.length > 0 && (
                <button type="button" className="property-back-link" onClick={() => setMainView('my-properties')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
                  {t('dashboard.myProperties')}
                </button>
              )}
              {isNative && mainView === 'properties' && (
                <div className="native-detail-head">
                  {nativeLockProperties.length > 1 && (
                    <button type="button" className="native-detail-back" onClick={() => setNativePropertyDetail(false)} aria-label={t('propertyFilter.backToAll')}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
                    </button>
                  )}
                  <h2 className="native-detail-title">{selectedProperty.name}</h2>
                </div>
              )}
              {/* Eén enkel tabblad (enkel Kalender) heeft geen tabknoppen nodig. */}
              <div
                className="property-tab-buttons"
                style={(nativeLockMode || (
                  !(user.hasAutomation && selectedProperty.automationEnabled)
                  && !(user.hasSubscription && selectedProperty.accessManagementEnabled)
                )) ? { display: 'none' } : undefined}
              >
                {/* Smart lock/Codes enkel als deze specifieke woning gekoppeld
                    staat aan het toegangsbeheer-abonnement
                    (accessManagementEnabled, door de admin per woning
                    ingesteld) - zonder die koppeling is er hier geen smart
                    lock/code om te beheren. Zelfde principe voor Automation
                    hieronder (automationEnabled). Volgorde bewust: Automation,
                    Kalender, Smart lock, Codes. */}
                {user.hasAutomation && selectedProperty.automationEnabled && (
                  <button className={`property-tab-btn ${activeTab === 'automation' ? 'active' : ''}`} onClick={() => setActiveTab('automation')}>
                    {t('dashboard.tabAutomation')}
                  </button>
                )}
                <button className={`property-tab-btn ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => setActiveTab('calendar')}>
                  {t('dashboard.tabCalendar')}
                </button>
                {user.hasSubscription && selectedProperty.accessManagementEnabled && (
                  <button className={`property-tab-btn ${activeTab === 'smartlock' ? 'active' : ''}`} onClick={() => setActiveTab('smartlock')}>
                    {t('dashboard.tabSmartlock')}
                  </button>
                )}
              </div>

              {activeTab === 'calendar' && (
                <PropertyCalendar
                  propertyId={selectedProperty.id}
                  propertyName={selectedProperty.name}
                  userRole={user.role}
                  cleaningTier={selectedProperty.cleaningTier}
                  cleaningHours={selectedProperty.cleaningHours}
                  cleaningServices={selectedProperty.cleaningServices}
                />
              )}

              {activeTab === 'smartlock' && user.hasSubscription && selectedProperty.accessManagementEnabled && (
                <div className="codes-section">
                  <div className="code-card smartlock-config-card">
                    <div className="code-header">
                      <span className="code-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span className="code-label">{t('dashboard.smartlockConfigTitle')}</span>
                      <button
                        type="button"
                        className="log-toggle-btn"
                        onClick={() => setShowSmartlockConfig((v) => !v)}
                      >
                        {showSmartlockConfig ? t('common.hide') : t('common.edit')}
                      </button>
                    </div>

                    {!showSmartlockConfig && (
                      <p className="battery-note">
                        {smartlockStatus?.linked
                          ? t('dashboard.smartlockConfigLinkedNote', { brand: SMARTLOCK_BRAND_LABELS[smartlockStatus.smartlockBrand] || (smartlockStatus.smartlockBrand || '-') })
                          : t('dashboard.smartlockConfigNoneNote')}
                      </p>
                    )}

                    {showSmartlockConfig && (
                      <form className="smartlock-config-form" onSubmit={handleSaveSmartlockConfig}>
                        <p className="battery-note">{t('dashboard.smartlockConfigHint')}</p>
                        <div className="form-group">
                          <label>{t('dashboard.smartlockBrandLabel')}</label>
                          <select
                            value={smartlockConfigForm.smartlockBrand}
                            onChange={(e) => setSmartlockConfigForm({ ...smartlockConfigForm, smartlockBrand: e.target.value })}
                          >
                            <option value="">{t('dashboard.smartlockBrandChoose')}</option>
                            <option value="NUKI">Nuki</option>
                            <option value="TEDEE">Tedee</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>{t('dashboard.smartlockIdLabel')}</label>
                          <input
                            type="text"
                            value={smartlockConfigForm.nukiSmartlockId}
                            onChange={(e) => setSmartlockConfigForm({ ...smartlockConfigForm, nukiSmartlockId: e.target.value })}
                            placeholder={smartlockConfigForm.smartlockBrand === 'TEDEE' ? t('dashboard.smartlockIdPlaceholderTedee') : t('dashboard.smartlockIdPlaceholder')}
                          />
                        </div>
                        <div className="form-group">
                          <label>{smartlockConfigForm.smartlockBrand === 'TEDEE' ? t('dashboard.smartlockTokenLabelTedee') : t('dashboard.smartlockTokenLabel')}</label>
                          <input
                            type="text"
                            value={smartlockConfigForm.nukiApiToken}
                            onChange={(e) => setSmartlockConfigForm({ ...smartlockConfigForm, nukiApiToken: e.target.value })}
                            placeholder={smartlockStatus?.hasApiToken ? t('dashboard.smartlockTokenPlaceholderSet') : (smartlockConfigForm.smartlockBrand === 'TEDEE' ? t('dashboard.smartlockTokenPlaceholderNoneTedee') : t('dashboard.smartlockTokenPlaceholderNone'))}
                          />
                        </div>
                        {smartlockConfigError && <p className="battery-warning">{smartlockConfigError}</p>}
                        <button type="submit" className="lock-action-btn lock" disabled={smartlockConfigSaving}>
                          {smartlockConfigSaving ? t('common.saving') : t('common.save')}
                        </button>
                      </form>
                    )}
                  </div>

                  <div className="code-card lock-actions-card">
                    <div className="code-header">
                      <span className="code-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M8 11V7C8 4.8 9.8 3 12 3C14.2 3 16 4.8 16 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </span>
                      <span className="code-label">{t('dashboard.lockActionsTitle')}</span>
                      {smartlockStatus?.reachable && (
                        <div className={`door-current-status ${smartlockStatus.locked === true ? 'is-locked' : smartlockStatus.locked === false ? 'is-unlocked' : ''}`}>
                          {smartlockStatus.locked === true && t('dashboard.doorLocked')}
                          {smartlockStatus.locked === false && t('dashboard.doorUnlocked')}
                          {(smartlockStatus.locked === null || smartlockStatus.locked === undefined) && t('dashboard.doorStatusUnknown')}
                        </div>
                      )}
                    </div>
                    <div className="lock-actions-buttons">
                      <button
                        type="button"
                        className="lock-action-btn unlock"
                        onClick={() => handleSmartlockAction(1)}
                        disabled={lockActionLoading || !smartlockStatus?.reachable}
                      >
                        {t('dashboard.unlockButton')}
                      </button>
                      <button
                        type="button"
                        className="lock-action-btn lock"
                        onClick={() => handleSmartlockAction(2)}
                        disabled={lockActionLoading || !smartlockStatus?.reachable}
                      >
                        {t('dashboard.lockButton')}
                      </button>
                    </div>
                  </div>

                  <div className="code-card battery-card">
                    <div className="code-header">
                      <span className="code-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M21 10V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </span>
                      <span className="code-label">{t('dashboard.batteryStatus')}</span>
                      {!smartlockStatusLoading && smartlockStatus?.reachable && (
                        <button
                          type="button"
                          className="smartlock-sync-btn"
                          onClick={handleSyncSmartlock}
                          disabled={smartlockSyncing}
                        >
                          {smartlockSyncing ? t('dashboard.smartlockSyncing') : t('dashboard.smartlockSyncButton')}
                        </button>
                      )}
                    </div>

                    {smartlockStatusLoading && (
                      <p className="battery-note">{t('dashboard.statusLoading')}</p>
                    )}

                    {!smartlockStatusLoading && (!smartlockStatus || !smartlockStatus.linked) && (
                      <p className="battery-note">{t('dashboard.noSmartlockLinked')}</p>
                    )}

                    {!smartlockStatusLoading && smartlockStatus?.linked && !smartlockStatus.configured && (
                      <p className="battery-note">{t('dashboard.smartlockNotConfigured')}</p>
                    )}

                    {!smartlockStatusLoading && smartlockStatus?.linked && smartlockStatus.configured && !smartlockStatus.reachable && (
                      <p className="battery-note">{t('dashboard.smartlockUnreachable')}</p>
                    )}

                    {!smartlockStatusLoading && smartlockStatus?.reachable && smartlockStatus.batteryCharge != null && (
                      <>
                        <div className="battery-level">
                          <div className="battery-bar">
                            <div
                              className={`battery-fill ${smartlockStatus.batteryCharge <= 20 ? 'low' : smartlockStatus.batteryCharge <= 50 ? 'medium' : 'high'}`}
                              style={{ width: `${smartlockStatus.batteryCharge}%` }}
                            />
                          </div>
                          <span className="battery-percentage">{smartlockStatus.batteryCharge}%</span>
                        </div>
                        {smartlockStatus.batteryCharging && (
                          <p className="battery-note battery-charging-note">{t('dashboard.batteryCharging')}</p>
                        )}
                        {!smartlockStatus.batteryCharging && (smartlockStatus.batteryCritical || smartlockStatus.batteryCharge <= 20) && (
                          <div className="battery-warning">{t('dashboard.batteryLow')}</div>
                        )}
                      </>
                    )}

                    {!smartlockStatusLoading && smartlockStatus?.reachable && smartlockStatus.keypadPaired && (
                      <div className="keypad-battery-status">
                        {smartlockStatus.keypadBatteryCritical ? (
                          <div className="battery-warning">{t('dashboard.keypadBatteryLow')}</div>
                        ) : (
                          <p className="battery-note">{t('dashboard.keypadBatteryOk')}</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="code-card">
                    <div className="code-header">
                      <span className="code-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M12 7V12L15.5 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span className="code-label">{t('dashboard.openingHistory')}</span>
                      <button onClick={toggleSmartlockLog} className="log-toggle-btn">
                        {showSmartlockLog ? t('common.hide') : t('common.view')}
                      </button>
                    </div>

                    {showSmartlockLog && (
                      <>
                        {smartlockLogLoading && (
                          <p className="battery-note">{t('dashboard.historyLoading')}</p>
                        )}
                        {!smartlockLogLoading && (!smartlockStatus || !smartlockStatus.linked) && (
                          <p className="battery-note">{t('dashboard.noSmartlockLinked')}</p>
                        )}
                        {!smartlockLogLoading && smartlockStatus?.linked && smartlockLog.length === 0 && (
                          <p className="battery-note">{t('dashboard.noRecentActivity')}</p>
                        )}
                        {!smartlockLogLoading && smartlockStatus?.linked && smartlockLog.length > 0 && (
                          <>
                            {/* Enkel de events met een herkenbare naam (gastcode =
                                ingecheckt, een persoonlijke/schoonmaakcode = iemand
                                toegekomen) - dit is de lijst die er standaard toe
                                doet voor de verhuurder, i.p.v. elke losse
                                app-/systeemactie te tonen. */}
                            {importantSmartlockLog.length === 0 ? (
                              <p className="battery-note">{t('dashboard.noImportantActivity')}</p>
                            ) : (
                              <ul className="smartlock-log-list">
                                {importantSmartlockLog.map((entry, i) => (
                                  <li key={i} className="smartlock-log-item">
                                    <span className="log-action">{NUKI_ACTION_LABELS[entry.action] || t('dashboard.actionUnknown', { code: entry.action ?? '?' })}</span>
                                    <span className="log-name"> - {entry.name}</span>
                                    <span className="log-date">
                                      {entry.date ? new Date(entry.date).toLocaleString('nl-BE') : ''}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}

                            <button
                              type="button"
                              className="log-toggle-btn full-history-toggle"
                              onClick={() => setShowFullSmartlockLog((v) => !v)}
                            >
                              {showFullSmartlockLog ? t('common.hide') : t('dashboard.viewFullHistory')}
                            </button>

                            {showFullSmartlockLog && (
                              <ul className="smartlock-log-list full-history">
                                {smartlockLog.map((entry, i) => (
                                  <li key={i} className="smartlock-log-item">
                                    <span className="log-action">{NUKI_ACTION_LABELS[entry.action] || t('dashboard.actionUnknown', { code: entry.action ?? '?' })}</span>
                                    {entry.name && <span className="log-name"> - {entry.name}</span>}
                                    <span className="log-date">
                                      {entry.date ? new Date(entry.date).toLocaleString('nl-BE') : ''}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>

                  {/* Gastcode + persoonlijke codes: stonden voorheen in een
                      eigen "Codes"-tab, nu samengevoegd in Smart lock, meteen
                      onder Acties - alles rond het slot bij elkaar i.p.v.
                      verspreid over 2 tabbladen. */}
                  <div className="code-card guest-code-card">
                    <div className="code-header">
                      <span className="code-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C8.13 2 5 5.13 5 9V12C3.5 12.5 3 13 3 15V18C3 19.1 3.9 20 5 20H19C20.1 20 21 19.1 21 18V15C21 13 20.5 12.5 19 12V9C19 5.13 15.87 2 12 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M9 22L12 20L15 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </span>
                      <span className="code-label">{t('dashboard.currentGuestCode')}</span>
                    </div>
                    {currentGuestCodeLoading && (
                      <p className="battery-note">{t('common.fetching')}</p>
                    )}
                    {!currentGuestCodeLoading && (!currentGuestCode || !currentGuestCode.active) && (
                      <p className="battery-note">{t('dashboard.noGuestCheckedIn')}</p>
                    )}
                    {!currentGuestCodeLoading && currentGuestCode?.active && (
                      <>
                        <div className="code-value">{currentGuestCode.code || '••••••'}</div>
                        <div className="code-expiry">
                          {currentGuestCode.guestName ? `${currentGuestCode.guestName} - ` : ''}
                          {t('dashboard.expiresAtCheckout')}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="personal-codes-header">
                    <h3>{t('dashboard.personalCodes')}</h3>
                    {personalCodes.length < 4 && (
                      <button onClick={() => setShowAddCodeModal(true)} className="add-code-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        {t('dashboard.addCode')}
                      </button>
                    )}
                  </div>

                  <div className="codes-grid">
                    {personalCodes.map(code => (
                      <div key={code.id} className="code-card personal-code-card">
                        <div className="code-header">
                          <span className="code-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M20 21V19C20 16.8 18.2 15 16 15H8C5.8 15 4 16.8 4 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                            </svg>
                          </span>
                          <span className="code-label">{code.name}</span>
                        </div>
                        <div className="code-value">{code.code}</div>
                        <div className="code-actions">
                          <button onClick={() => openEditModal(code)} className="icon-btn" title={t('common.edit')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M17 3L21 7L7 21H3V17L17 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            </svg>
                          </button>
                          <button onClick={() => handleDeleteCode(code.id, code.name)} className="icon-btn delete-icon" title={t('common.delete')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 7H20M10 11V16M14 11V16M5 7L6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19L19 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              <path d="M9 7V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V7" stroke="currentColor" strokeWidth="1.5"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'automation' && user.hasAutomation && selectedProperty.automationEnabled && (
                <AutomationOverview propertyId={selectedProperty.id} propertyName={selectedProperty.name} />
              )}
            </>
          ) : (
            <div className="no-property-selected">
              <h2>{t('dashboard.noPropertySelected')}</h2>
              <p>{t('dashboard.selectPropertyHint')}</p>
            </div>
          )}
          </ErrorBoundary>
        </main>
      </div>

      {/* Modal voor woning bewerken */}
      {showEditPropertyModal && editingProperty && (
        <div className="modal-overlay" onClick={() => setShowEditPropertyModal(false)}>
          <div className="modal property-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('dashboard.editPropertyTitle')}</h2>
              <button className="modal-close" onClick={() => setShowEditPropertyModal(false)}>✖</button>
            </div>
            <form onSubmit={handleEditPropertySave} noValidate onInput={clearFieldInvalid}>
              <div className="form-group">
                <label>{t('common.name')}</label>
                <input
                  type="text"
                  value={editingProperty.name || ''}
                  onChange={(e) => setEditingProperty({...editingProperty, name: e.target.value})}
                  required
                />
              </div>

              {!isNative && (
              <div className="form-group">
                <label>{t('common.address')}</label>
                <input
                  type="text"
                  value={editingProperty.address || ''}
                  onChange={(e) => setEditingProperty({...editingProperty, address: e.target.value})}
                  placeholder={t('dashboard.addressPlaceholder')}
                />
              </div>
              )}

              <div className="form-group">
                <label>{t('dashboard.photoLabel')}</label>

                {photoError && (
                  <div className="checklist-modal-error">{photoError}</div>
                )}

                {editingProperty.photoUrl ? (
                  <div className="property-photo-current">
                    <img src={`${API_URL}${editingProperty.photoUrl}`} alt="" className="property-photo-preview" />
                    <button type="button" className="action-btn" onClick={handleDeletePhoto}>
                      {t('common.delete')}
                    </button>
                  </div>
                ) : (
                  <p className="empty-state-text">{t('dashboard.photoEmpty')}</p>
                )}

                <label className="submit-btn" style={{ display: 'inline-block', width: 'auto', cursor: photoUploading ? 'wait' : 'pointer', opacity: photoUploading ? 0.7 : 1 }}>
                  {photoUploading ? t('dashboard.photoUploading') : (editingProperty.photoUrl ? t('dashboard.photoReplace') : t('dashboard.photoUpload'))}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleUploadPhoto}
                    disabled={photoUploading}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              {editingProperty.cleaningTier !== 'NONE' && (
              <div className="form-group">
                <label>{t('dashboard.cleaningPdfLabel')}</label>

                {cleaningPdfError && (
                  <div className="checklist-modal-error">{cleaningPdfError}</div>
                )}

                {editingProperty.cleaningPdfOriginalName ? (
                  <div className="cleaning-pdf-current">
                    <span className="cleaning-pdf-filename">📄 {editingProperty.cleaningPdfOriginalName}</span>
                    <div className="checklist-manage-item-actions">
                      <button type="button" className="action-btn" style={{ color: 'var(--primary)' }} onClick={handleViewCleaningPdf}>
                        {t('dashboard.cleaningPdfView')}
                      </button>
                      <button type="button" className="action-btn" onClick={handleDeleteCleaningPdf}>
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="empty-state-text">{t('dashboard.cleaningPdfEmpty')}</p>
                )}

                <label className="submit-btn" style={{ display: 'inline-block', width: 'auto', cursor: cleaningPdfUploading ? 'wait' : 'pointer', opacity: cleaningPdfUploading ? 0.7 : 1 }}>
                  {cleaningPdfUploading ? t('dashboard.cleaningPdfUploading') : (editingProperty.cleaningPdfOriginalName ? t('dashboard.cleaningPdfReplace') : t('dashboard.cleaningPdfUpload'))}
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleUploadCleaningPdf}
                    disabled={cleaningPdfUploading}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
              )}

              {/* De Kalenderkoppeling-sectie (Guesty/Hospitable-status,
                  legacy platform-links) is bewust volledig verwijderd uit de
                  verhuurder-modal, niet enkel niet-bewerkbaar gemaakt: een
                  klant heeft niets aan die technische koppelstatus en hoeft
                  niet te weten via welk systeem (Guesty/Hospitable/rechtstreeks)
                  zijn kalender precies loopt. Enkel SmartStays (Admin.js)
                  beheert en ziet deze koppelingen nog. */}

              <div className="modal-buttons">
                <button type="button" onClick={() => setShowEditPropertyModal(false)} className="cancel-btn">{t('common.cancel')}</button>
                <button type="submit" className="submit-btn">{t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal voor code wijzigen */}
      {showCodeModal && (
        <div className="modal-overlay" onClick={() => setShowCodeModal(false)}>
          <div className="modal code-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('dashboard.editCodeTitle')}</h2>
              <button className="modal-close" onClick={() => setShowCodeModal(false)}>✖</button>
            </div>
            <div className="modal-body">
              <div className="warning-box">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                  <path d="M12 8V12M12 16H12.01M3 12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12Z" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <span>{t('dashboard.editCodeWarning')}</span>
              </div>
              <div className="form-group">
                <label>{t('common.name')}</label>
                <input type="text" value={newCodeName} onChange={(e) => setNewCodeName(e.target.value)} placeholder={t('dashboard.codeNamePlaceholder1')} />
              </div>
              <div className="form-group">
                <label>{t('dashboard.newCodeLabel')}</label>
                <input
                  type="text"
                  value={newCodeValue}
                  onChange={(e) => setNewCodeValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength="6"
                  className="code-input"
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                />
              </div>
              <div className="form-group">
                <label>{t('dashboard.repeatCodeLabel')}</label>
                <input
                  type="text"
                  value={confirmCodeValue}
                  onChange={(e) => setConfirmCodeValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength="6"
                  className="code-input"
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                />
              </div>
              {codeError && <div className="error-message">{codeError}</div>}
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowCodeModal(false)} className="cancel-btn">{t('common.cancel')}</button>
              <button onClick={handleEditCode} className="submit-btn">{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal voor nieuwe code toevoegen */}
      {showAddCodeModal && (
        <div className="modal-overlay" onClick={() => setShowAddCodeModal(false)}>
          <div className="modal code-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('dashboard.addCodeTitle')}</h2>
              <button className="modal-close" onClick={() => setShowAddCodeModal(false)}>✖</button>
            </div>
            <div className="modal-body">
              <div className="warning-box">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                  <path d="M12 8V12M12 16H12.01M3 12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12Z" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <span>{t('dashboard.addCodeWarning')}</span>
              </div>
              <div className="form-group">
                <label>{t('dashboard.nameOptional')}</label>
                <input type="text" value={newCodeName} onChange={(e) => setNewCodeName(e.target.value)} placeholder={t('dashboard.codeNamePlaceholder2')} />
              </div>
              <div className="form-group">
                <label>{t('dashboard.codeLabel')}</label>
                <input
                  type="text"
                  value={newCodeValue}
                  onChange={(e) => setNewCodeValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength="6"
                  className="code-input"
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                />
              </div>
              <div className="form-group">
                <label>{t('dashboard.repeatCodeLabel')}</label>
                <input
                  type="text"
                  value={confirmCodeValue}
                  onChange={(e) => setConfirmCodeValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength="6"
                  className="code-input"
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                />
              </div>
              {codeError && <div className="error-message">{codeError}</div>}
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowAddCodeModal(false)} className="cancel-btn">{t('common.cancel')}</button>
              <button onClick={handleAddCode} className="submit-btn">{t('dashboard.addBtn')}</button>
            </div>
          </div>
        </div>
      )}

      {isNative && createPortal(
        <nav className="native-tabbar">
          <button
            type="button"
            className={`native-tab ${mainView === 'multi-calendar' ? 'active' : ''}`}
            aria-label={t('dashboard.tabCalendar')}
            onClick={() => setMainView('multi-calendar')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M3 10H21M8 2V6M16 2V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
          </button>
          {hasCleaning && (
          <button
            type="button"
            className={`native-tab ${mainView === 'cleaning' ? 'active' : ''}`}
            aria-label={t('dashboard.navCleaning')}
            onClick={() => setMainView('cleaning')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M11 3L12.6 8.4L18 10L12.6 11.6L11 17L9.4 11.6L4 10L9.4 8.4L11 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M18 14L18.7 16.3L21 17L18.7 17.7L18 20L17.3 17.7L15 17L17.3 16.3L18 14Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>
          </button>
          )}
          {hasLock && (
          <button
            type="button"
            className={`native-tab ${mainView === 'properties' ? 'active' : ''}`}
            aria-label={t('dashboard.tabSmartlock')}
            onClick={() => { setMainView('properties'); setNativePropertyDetail(false); }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4.5" y="10.5" width="15" height="10" rx="2.2" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /><circle cx="12" cy="15.5" r="1.3" /></svg>
          </button>
          )}
          {user?.hasAutomation && (
            <button
              type="button"
              className={`native-tab ${mainView === 'automation' ? 'active' : ''}`}
              aria-label={t('dashboard.navAutomation')}
              onClick={() => setMainView('automation')}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 19V13M10 19V9M16 19V5M22 19H2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          )}
          <button
            type="button"
            className={`native-tab ${mainView === 'notifications' ? 'active' : ''}`}
            aria-label={t('notifications.title')}
            onClick={() => setMainView('notifications')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
            {nativeUnread > 0 && <span className="native-tab-badge">{nativeUnread > 99 ? '99+' : nativeUnread}</span>}
          </button>
          <button
            type="button"
            className={`native-tab ${mainView === 'profile' || mainView === 'my-properties' ? 'active' : ''}`}
            aria-label={t('profileMenu.title')}
            onClick={() => setMainView('profile')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg>
          </button>
        </nav>,
        document.body
      )}
    </div>
  );
}

export default Dashboard;
